/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MenuData, MenuLevel } from '../models';
import Icon from '@lib/components/Icon';
import getMetaMenu from '../meta-menu';
import { getLevel } from '../menu-helpers';
import isDesktop from '@lib/is-desktop';
import { MenuBlock } from '../MenuBlock';

interface MobileMenuProps {
    data: MenuData;
}

export function MobileMenu({ data }: MobileMenuProps): JSX.Element {
    const [activeLens, setActiveLens] = useState<number[] | null>(null);

    const toggleMenu = useCallback(() => {
        setActiveLens(activeLens ? null : []);
    }, [activeLens, setActiveLens]);

    const parent: MenuLevel | null = useMemo(() => {
        if (activeLens && activeLens.length) return getLevel(data, activeLens);
        else return null;
    }, [activeLens, data]);

    const activeMenuLinks: MenuLevel[] | null = useMemo(() => {
        if (activeLens && activeLens.length) return getLevel(data, activeLens)?.children || null;
        else if (activeLens) return data;
        else return null;
    }, [activeLens, data]);

    const blockType: string = useMemo(() => {
        return activeLens && activeLens[0] ? data[activeLens[0]]!.blockType : '';
    }, [activeLens]);

    const activate = useCallback(
        (e: React.MouseEvent<HTMLSpanElement | HTMLAnchorElement>, element: MenuLevel, index: number) => {
            if (element.children) {
                e.preventDefault();
                setActiveLens((lens) => [...lens!, index]);
            }
        },
        [setActiveLens]
    );

    const back = useCallback(() => {
        setActiveLens((lens) => (lens ? lens.slice(0, -1) : null));
    }, [setActiveLens]);

    useEffect(() => {
        if (activeLens) document.documentElement.classList.add('mobile-menu-open');
        else document.documentElement.classList.remove('mobile-menu-open');
    }, [activeLens]);

    return (
        <div className="mobile-menu">
            <button className="button button--icon has-darkblue-icon" onClick={toggleMenu}>
                <Icon icon={activeLens ? 'close' : 'hamburger'} />
            </button>
            {activeMenuLinks && (
                <ul className="mobile-menu__list">
                    {parent && (
                        <li className="mobile-menu__list-element">
                            <button type="button" className="mobile-menu__back-button" onClick={back}>
                                <Icon icon="arrow-left" />
                                <span>Back</span>
                            </button>
                        </li>
                    )}
                    {parent && parent.href && (
                        <li className="mobile-menu__list-element">
                            <MobileMenuElement href={parent.href} parent>
                                {parent.text}
                            </MobileMenuElement>
                        </li>
                    )}
                    {activeMenuLinks.map((element, index) => (
                        <li key={element.text + element.href + index} className="mobile-menu__list-element">
                            <MobileMenuElement
                                isExternal={element.external}
                                href={element.href}
                                onClick={(e) => activate(e, element, index)}
                            >
                                <span>{element.text}</span>
                            </MobileMenuElement>
                        </li>
                    ))}
                    {!parent && <MetaMenuRenderer />}
                    {blockType && (
                        <li className="mobile-menu__list-block">
                            <MenuBlock type={blockType} />
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}

interface MobileMenuElementProps extends HTMLAttributes<HTMLAnchorElement> {
    isExternal?: boolean;
    href?: string;
    parent?: boolean;
    children: React.ReactChildren | React.ReactChild;
}

function MobileMenuElement({ isExternal, href, parent, children, ...props }: MobileMenuElementProps): JSX.Element {
    return (
        <a {...props} className="mobile-menu__list-link" href={href}>
            <span>{children}</span>
            <Icon icon={parent ? 'waffle' : isExternal ? 'external-link' : 'arrow-right'} />
        </a>
    );
}

function MetaMenuRenderer(): JSX.Element {
    const ref = useRef<HTMLLIElement>(null);

    useEffect(() => {
        const append = () => ref.current!.appendChild(getMetaMenu()!);
        const onresize = () => !isDesktop() && append();
        window.addEventListener('resize', onresize, { passive: true });
        append();
        return () => {
            window.removeEventListener('resize', onresize);
        };
    }, []);

    return <li ref={ref} className="mobile-menu__meta-menu" />;
}
